/*
 * Copyright (c) 2025 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import ArgosDialog from "../../../../../../../components/Dialogs/ArgosDialog"
import ArgosFlexbox from "../../../../../../../components/Containers/ArgosFlexbox";
import {Tooltip, Typography} from "@mui/material";
import ArgosButton from "../../../../../../../components/Buttons/ArgosButton";
import {SENSING_SYSTEM_ARCHITECTURE_DESIGN_CONSTANTS} from "../../../../../constants";
import {usePostRequest} from "../../../../../../../../api/helper";
import {useEffect, useState} from "react";
import {toast} from "react-hot-toast";
import InvalidObjectsDisplay from "./InvalidObjectsDisplay";

interface IProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (sensorIdsToGenerate: Record<string, true>) => void;
  data: any;
}

const CheckObjectsBeforeBluelineGenerationDialog = (props: IProps) => {
  const {selectedSensorsForGreenlineModify, studyId, greenlineIdToProperties} = props.data;

  const checkIfObjectsValidForBlueLineGenerationUrl = SENSING_SYSTEM_ARCHITECTURE_DESIGN_CONSTANTS.viewStudy.endpoints.checkIfObjectsValidForBlueLineGeneration;
  const checkIfObjectsValidForBlueLineGenerationHandle = usePostRequest(checkIfObjectsValidForBlueLineGenerationUrl);
  const [isLoadingCheckIfObjectsValidForBlueLineGeneration, setIsLoadingCheckIfObjectsValidForBlueLineGeneration] = useState(false);
  const [responseOfCheckIfObjectsValidForBlueLineGeneration, setResponseOfCheckIfObjectsValidForBlueLineGeneration] = useState<any>(null);
  const checkIfObjectsValidForBlueLineGeneration = () => {
    setIsLoadingCheckIfObjectsValidForBlueLineGeneration(true)
    checkIfObjectsValidForBlueLineGenerationHandle({
      selectedSensorsForGreenlineModify: selectedSensorsForGreenlineModify, studyId
    }).then((response) => {
      console.log('Objects validity check for blue line generation response:', response);
      setResponseOfCheckIfObjectsValidForBlueLineGeneration(response)
    }).catch((error) => {
      console.error('Error checking objects validity for blue line generation:', error);
      toast.error('Error checking objects validity for blue line generation.');
      setResponseOfCheckIfObjectsValidForBlueLineGeneration(null)
    }).finally(() => {
      setIsLoadingCheckIfObjectsValidForBlueLineGeneration(false);
    })
  }

  useEffect(() => {
    if (!props.open) return;
    checkIfObjectsValidForBlueLineGeneration()
  }, [props.open]);


  const idsData = responseOfCheckIfObjectsValidForBlueLineGeneration?.idsData;

  const validSensorIds = !selectedSensorsForGreenlineModify ? [] : Object.keys(selectedSensorsForGreenlineModify).filter((sensorId: string) => {
    // If not in idsData, all objects are valid
    if (!idsData || !(sensorId in idsData)) return true;
    // If in idsData, check allObjectsInvalid
    return !idsData[sensorId].allObjectsInvalid;
  });

  const noSensorsWithValidObjects = idsData && Object.keys(idsData).length > 0 && validSensorIds.length === 0;

  const sensorIdsToGenerate: Record<string, true> = {};
  validSensorIds.forEach((id: string) => {
    sensorIdsToGenerate[id] = true;
  });


  const disabledGenerateButtonMessage = isLoadingCheckIfObjectsValidForBlueLineGeneration ?
    'Checking objects\' validity for blue line generation...' : responseOfCheckIfObjectsValidForBlueLineGeneration == null ? 'Objects check failed' :
    'There are no sensors with at least one valid object.';
  const disabledGenerateButton = responseOfCheckIfObjectsValidForBlueLineGeneration == null || noSensorsWithValidObjects || isLoadingCheckIfObjectsValidForBlueLineGeneration;

  const loadingCheckObjectsMesssage = 'Verifying objects before generating blue lines. Please wait.'

  return (
    <>
      <ArgosDialog
        height={'auto'}
        maxHeight={'70vh'}
        width={'auto'}
        open={props.open}
        headerTitle={'Generate Blue Lines'}
        headerTitleAlign={'left'}
      >
        <ArgosFlexbox vertical style={{padding: '20px', overflow: 'auto', height: '100%'}}>
          <ArgosFlexbox vertical style={{padding: '20px', overflow: 'auto', flex: 1}}>
            <ArgosFlexbox vertical style={{overflow: 'auto', flex: 1}}>
              {isLoadingCheckIfObjectsValidForBlueLineGeneration ? <Typography variant="h6">{loadingCheckObjectsMesssage}</Typography> :
                <InvalidObjectsDisplay
                  idsData={idsData}
                  objectIdToName={responseOfCheckIfObjectsValidForBlueLineGeneration?.objectIdToName}
                  greenlineIdToProperties={greenlineIdToProperties}
                />
              }
            </ArgosFlexbox>
          </ArgosFlexbox>
          <ArgosFlexbox style={{display: 'flex', flexDirection: 'row', padding: '10px'}}>
            <ArgosButton
              id={`cancel-button`}
              data-testid={`cancel-button`}
              variant="contained"
              size="large"
              fontSize={16}
              onClick={props.onClose}
            >
              Cancel
            </ArgosButton>
            <Tooltip
              title={disabledGenerateButtonMessage}
              disableHoverListener={!disabledGenerateButton}
            >
            <span>
              <ArgosButton
                id={`confirm-button`}
                data-testid={`confirm-button`}
                variant="text"
                size="large"
                fontSize={16}
                onClick={()=>props.onConfirm(sensorIdsToGenerate)}
                disabled={disabledGenerateButton}
              >
              Generate
            </ArgosButton>
            </span>
            </Tooltip>
          </ArgosFlexbox>
        </ArgosFlexbox>
      </ArgosDialog>
    </>
  )
}

export default CheckObjectsBeforeBluelineGenerationDialog